import React, { useState } from 'react';
import {
  FiChevronDown, FiChevronRight, FiDownload, FiPlay, FiCopy, FiCheck, FiAlertTriangle, FiCode
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import ApiTestRunner from './ApiTestRunner';

const METHOD_STYLES = {
  GET:     { bg: 'bg-sky-50',     text: 'text-sky-700',     border: 'border-sky-200' },
  POST:    { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  PUT:     { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200' },
  PATCH:   { bg: 'bg-purple-50',  text: 'text-purple-700',  border: 'border-purple-200' },
  DELETE:  { bg: 'bg-rose-50',    text: 'text-rose-700',    border: 'border-rose-200' },
  OPTIONS: { bg: 'bg-slate-100',  text: 'text-slate-700',   border: 'border-slate-200' },
};

export function ApiEndpointCard({ endpoint, documentId, canDownload, canTest, onDownloadEndpoint }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [copiedPath, setCopiedPath] = useState(false);

  const method = endpoint.httpMethod?.toUpperCase() || 'GET';
  const style = METHOD_STYLES[method] || METHOD_STYLES.GET;
  const spec = endpoint.endpointSpec;

  const copyPath = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(endpoint.path);
    setCopiedPath(true);
    toast.success('Path copied');
    setTimeout(() => setCopiedPath(false), 2000);
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    if (canDownload && endpoint.id) {
      onDownloadEndpoint(endpoint.id);
    }
  };

  const parameters = Array.isArray(spec?.parameters) ? spec.parameters : [];
  const requestBodyContent = spec?.requestBody?.content;
  const jsonRequestBody = requestBodyContent?.['application/json'];
  const responses = spec?.responses || {};

  return (
    <div
      className={`rounded-xl border transition-all duration-200 bg-white shadow-sm overflow-hidden ${
        isOpen ? 'border-blue-300 ring-2 ring-blue-500/10' : 'border-slate-200 hover:border-slate-300'
      }`}
    >
      {/* Header bar */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 p-3.5 cursor-pointer select-none hover:bg-slate-50/50 transition-colors"
      >
        <span
          className={`w-18 shrink-0 px-2.5 py-1 text-center font-mono font-bold text-xs rounded-md border ${style.bg} ${style.text} ${style.border}`}
        >
          {method}
        </span>

        <span className="font-mono text-sm font-semibold text-slate-900 flex-1 truncate">
          {endpoint.path}
        </span>

        {endpoint.summary && (
          <span className="hidden md:inline-block text-xs text-slate-500 max-w-sm truncate">
            {endpoint.summary}
          </span>
        )}

        {endpoint.isDeprecated && (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
            <FiAlertTriangle size={11} /> Deprecated
          </span>
        )}

        <div className="flex items-center gap-1.5 shrink-0 ml-auto" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={copyPath}
            title="Copy Endpoint Path"
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
          >
            {copiedPath ? <FiCheck size={14} className="text-emerald-600" /> : <FiCopy size={14} />}
          </button>

          {canDownload && endpoint.id && (
            <button
              onClick={handleDownload}
              title="Download Endpoint JSON specification"
              className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition"
            >
              <FiDownload size={14} />
            </button>
          )}

          <div
            onClick={() => setIsOpen(!isOpen)}
            className="p-1 text-slate-400 hover:text-slate-600"
          >
            {isOpen ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
          </div>
        </div>
      </div>

      {/* Expanded Accordion Body */}
      {isOpen && (
        <div className="px-5 pb-5 pt-3 border-t border-slate-100 bg-slate-50/30 space-y-4">
          {/* Sub Navigation Tabs: Details vs Try It Out */}
          <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
            <button
              onClick={() => setActiveTab('details')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
                activeTab === 'details'
                  ? 'bg-white text-slate-900 shadow-sm border border-slate-200'
                  : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
              }`}
            >
              <FiCode size={13} />
              <span>Specification</span>
            </button>
            <button
              onClick={() => setActiveTab('test')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
                activeTab === 'test'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
              }`}
            >
              <FiPlay size={13} />
              <span>Try It Out</span>
            </button>
          </div>

          {activeTab === 'test' ? (
            <ApiTestRunner
              endpoint={endpoint}
              canTest={canTest}
            />
          ) : (
            <div className="space-y-4">
              {/* Description */}
              {endpoint.description && (
                <div>
                  <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">Description</p>
                  <p className="text-xs text-slate-700 leading-relaxed">
                    {endpoint.description}
                  </p>
                </div>
              )}

              {/* Tags & Operation ID */}
              <div className="flex flex-wrap items-center gap-2 text-xs">
                {endpoint.operationId && (
                  <span className="font-mono text-slate-600 bg-white border border-slate-200 px-2 py-0.5 rounded text-[11px]">
                    operationId: {endpoint.operationId}
                  </span>
                )}
                {endpoint.tags &&
                  endpoint.tags.split(',').map((t) => (
                    <span
                      key={t}
                      className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-medium"
                    >
                      {t.trim()}
                    </span>
                  ))}
              </div>

              {/* Parameters Table */}
              {parameters.length > 0 && (
                <div>
                  <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">
                    Parameters ({parameters.length})
                  </p>
                  <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-50 text-slate-600 font-bold border-b border-slate-200">
                        <tr>
                          <th className="p-3">Name</th>
                          <th className="p-3">In</th>
                          <th className="p-3">Type</th>
                          <th className="p-3">Required</th>
                          <th className="p-3">Description</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 font-mono">
                        {parameters.map((p, idx) => (
                          <tr key={idx} className="hover:bg-slate-50/50">
                            <td className="p-3 font-bold text-slate-900">{p.name}</td>
                            <td className="p-3 text-slate-500">{p.in}</td>
                            <td className="p-3 text-blue-600">
                              {p.schema?.type || p.type || 'string'}
                            </td>
                            <td className="p-3">
                              {p.required ? (
                                <span className="text-rose-700 bg-rose-50 border border-rose-200/60 font-semibold px-1.5 py-0.5 rounded text-[10px]">
                                  required
                                </span>
                              ) : (
                                <span className="text-slate-400">optional</span>
                              )}
                            </td>
                            <td className="p-3 font-sans text-slate-600">
                              {p.description || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Request Body */}
              {jsonRequestBody && (
                <div>
                  <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                    Request Body (application/json)
                    {spec.requestBody?.required && (
                      <span className="text-rose-600 ml-1 font-semibold">*required</span>
                    )}
                  </p>
                  <div className="rounded-lg border border-slate-800 bg-slate-900 p-3.5 overflow-x-auto text-xs font-mono text-emerald-400">
                    <pre>
                      {JSON.stringify(
                        jsonRequestBody.example || jsonRequestBody.schema || {},
                        null,
                        2
                      )}
                    </pre>
                  </div>
                </div>
              )}

              {/* Responses List */}
              {Object.keys(responses).length > 0 && (
                <div>
                  <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">
                    Responses
                  </p>
                  <div className="space-y-2.5">
                    {Object.entries(responses).map(([code, resp]) => (
                      <div
                        key={code}
                        className="rounded-lg border border-slate-200 p-3.5 bg-white shadow-sm text-xs"
                      >
                        <div className="flex items-center gap-2 mb-1.5">
                          <span
                            className={`font-mono font-bold px-2 py-0.5 rounded text-[11px] border ${
                              code.startsWith('2')
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : code.startsWith('4')
                                ? 'bg-amber-50 text-amber-700 border-amber-200'
                                : 'bg-rose-50 text-rose-700 border-rose-200'
                            }`}
                          >
                            {code}
                          </span>
                          <span className="text-slate-800 font-semibold">
                            {resp.description || 'Response'}
                          </span>
                        </div>
                        {resp.content?.['application/json']?.schema && (
                          <div className="mt-2 text-slate-200 font-mono text-[11px] bg-slate-900 p-3 rounded-lg border border-slate-800 overflow-x-auto">
                            <pre>
                              {JSON.stringify(
                                resp.content['application/json'].example ||
                                  resp.content['application/json'].schema,
                                null,
                                2
                              )}
                            </pre>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ApiEndpointCard;
